import { useTranslation } from "react-i18next";
import { useAppSelector } from "../store";

export interface MenuItem {
    title: string;
    path: string;
    icon: {
        default: string;
        active: string;
    };
    roles?: string[];
    children?: MenuItem[];
    badge?: string;
}

export const useSidebarMenu = () => {
    const { t } = useTranslation();
    const { user } = useAppSelector((state) => state.auth);
    const userRoles = user?.roles || [];

    const getMenuItems = (): MenuItem[] => {
        if (userRoles.includes("admin") || userRoles.includes("superadmin")) {
            return [
                {
                    title: t("dashboard"),
                    path: "/dashboard",
                    icon: {
                        default: "tb-layout2",
                        active: "tb-layout2-filled",
                    },
                },
                {
                    title: t("marketing_dashboard"),
                    path: "/marketing-dashboard",
                    icon: {
                        default: "tb-chart-line",
                        active: "tb-chart-line-filled",
                    },
                },
                {
                    title: t("companies"),
                    path: "/companies",
                    icon: {
                        default: "tb-buildings",
                        active: "tb-buildings-filled",
                    },
                },
                {
                    title: t("users"),
                    path: "/users",
                    icon: {
                        default: "pi-users",
                        active: "pi-users-filled",
                    },
                },
                {
                    title: t("ai_chatbots"),
                    path: "/ai-chatbots",
                    icon: {
                        default: "rb-chatbot",
                        active: "rb-chatbot-filled",
                    },
                },
                {
                    title: t("booking_management_system"),
                    path: "/bms/bookings",
                    icon: {
                        default: "tb-calendar-event",
                        active: "tb-calendar-event-filled",
                    },
                    children: [
                        { title: t("bms_users"), path: "/bms/users", icon: { default: "tb-users", active: "tb-users-filled" } },
                        { title: t("bms_customers"), path: "/bms/customers", icon: { default: "tb-user", active: "tb-user-filled" } },
                        { title: t("bms_services"), path: "/bms/services", icon: { default: "tb-briefcase", active: "tb-briefcase-filled" } },
                        { title: t("bms_bookings"), path: "/bms/bookings", icon: { default: "tb-list", active: "tb-list" } },
                        { title: t("bms_booking_configurations"), path: "/bms/booking-configurations", icon: { default: "tb-settings", active: "tb-settings-filled" } },
                        { title: t("bms_calendar"), path: "/bms/calendar", icon: { default: "tb-calendar", active: "tb-calendar" } },
                    ],
                },
                {
                    title: "Usage",
                    path: "/usage",
                    icon: {
                        default: "tb-chart-bar",
                        active: "tb-chart-bar-filled",
                    },
                },
                {
                    title: t("ai_tools"),
                    path: "/ai-tools",
                    icon: {
                        default: "tb-cpu",
                        active: "tb-cpu-filled",
                    },
                },
                {
                    title: t("module_management"),
                    path: "/admin/module-management",
                    icon: {
                        default: "tb-adjustments-horizontal",
                        active: "tb-adjustments-horizontal",
                    },
                    children: [
                        {
                            title: t("mod_assignments"),
                            path: "/admin/module-management",
                            icon: {
                                default: "tb-plug-connected",
                                active: "tb-plug-connected",
                            },
                        },
                        {
                            title: t("mod_catalog_title"),
                            path: "/admin/module-catalog",
                            icon: {
                                default: "tb-puzzle",
                                active: "tb-puzzle-filled",
                            },
                        },
                    ],
                },
                {
                    title: t("subscriptions"),
                    path: "/admin/subscriptions",
                    icon: {
                        default: "tb-credit-card",
                        active: "tb-credit-card-filled",
                    },
                    children: [
                        {
                            title: t("plans"),
                            path: "/admin/plans",
                            icon: {
                                default: "tb-list",
                                active: "tb-list",
                            },
                        },
                        {
                            title: t("subscriptions"),
                            path: "/admin/subscriptions",
                            icon: {
                                default: "tb-users",
                                active: "tb-users-filled",
                            },
                        },
                        {
                            title: t("payments"),
                            path: "/admin/payments",
                            icon: {
                                default: "tb-receipt",
                                active: "tb-receipt-filled",
                            },
                        },
                    ],
                },
                {
                    title: t("settings"),
                    path: "/settings",
                    icon: {
                        default: "tb-settings",
                        active: "tb-settings-filled",
                    },
                },
            ];
        }

        if (userRoles.includes("agency_owner")) {
            return [
                {
                    title: t("dashboard"),
                    path: "/dashboard",
                    icon: {
                        default: "tb-layout2",
                        active: "tb-layout2-filled",
                    },
                },
                {
                    title: t("marketing_dashboard"),
                    path: "/marketing-dashboard",
                    icon: {
                        default: "tb-chart-line",
                        active: "tb-chart-line-filled",
                    },
                },
                {
                    title: t("my_agency"),
                    path: "/my-agency",
                    icon: {
                        default: "bi-building",
                        active: "bi-building-filled",
                    },
                    children: [
                        {
                            title: t("agency_details"),
                            path: "/my-agency/profile",
                            icon: {
                                default: "tb-building-cog",
                                active: "tb-building-cog",
                            },
                        },
                        {
                            title: t("client_companies"),
                            path: "/client-companies",
                            icon: {
                                default: "tb-buildings",
                                active: "tb-buildings-filled",
                            },
                        },
                        {
                            title: t("my_team"),
                            path: "/my-agency/my-team",
                            icon: {
                                default: "tb-users",
                                active: "tb-users-filled",
                            },
                        },
                    ],
                },
                {
                    title: t("ai_chatbots"),
                    path: "/ai-chatbots",
                    icon: {
                        default: "rb-chatbot",
                        active: "rb-chatbot-filled",
                    },
                },
                {
                    title: t("bms"),
                    path: "/bms/bookings",
                    icon: {
                        default: "tb-calendar-event",
                        active: "tb-calendar-event-filled",
                    },
                    children: [
                        { title: t("bms_users"), path: "/bms/users", icon: { default: "tb-users", active: "tb-users-filled" } },
                        { title: t("bms_customers"), path: "/bms/customers", icon: { default: "tb-user", active: "tb-user-filled" } },
                        { title: t("bms_services"), path: "/bms/services", icon: { default: "tb-briefcase", active: "tb-briefcase-filled" } },
                        { title: t("bms_bookings"), path: "/bms/bookings", icon: { default: "tb-list", active: "tb-list" } },
                        { title: t("bms_booking_configurations"), path: "/bms/booking-configurations", icon: { default: "tb-settings", active: "tb-settings-filled" } },
                        { title: t("bms_calendar"), path: "/bms/calendar", icon: { default: "tb-calendar", active: "tb-calendar" } },
                    ],
                },
                {
                    title: t("subscription"),
                    path: "/subscription",
                    icon: {
                        default: "tb-credit-card",
                        active: "tb-credit-card-filled",
                    },
                },
            ];
        }

        // Company Owner & Specialist
        if (userRoles.includes("company_owner") || userRoles.includes("company_specialist")) {
            return [
                {
                    title: t("dashboard"),
                    path: "/dashboard",
                    icon: {
                        default: "tb-layout2",
                        active: "tb-layout2-filled",
                    },
                },
                ...(userRoles.includes("company_owner")
                    ? [{
                        title: t("my_company") || "My Company",
                        path: "/my-company",
                        icon: {
                            default: "bi-building",
                            active: "bi-building-filled",
                        },
                        children: [
                            { title: t("company_details") || "Company Details", path: "/my-company/profile", icon: { default: "tb-building-cog", active: "tb-building-cog" } },
                            { title: t("my_team") || "My Team", path: "/my-company/my-team", icon: { default: "tb-users", active: "tb-users-filled" } },
                        ]
                    }]
                    : []),
                {
                    title: t("ai_chatbots"),
                    path: "/ai-chatbots",
                    icon: {
                         default: "rb-chatbot",
                        active: "rb-chatbot-filled",
                    },
                },
                ...(userRoles.includes("company_owner")
                    ? [{
                        title: t("bms"),
                        path: "/bms/bookings",
                        icon: {
                            default: "tb-calendar-event",
                            active: "tb-calendar-event-filled",
                        },
                        children: [
                            { title: t("bms_users"), path: "/bms/users", icon: { default: "tb-users", active: "tb-users-filled" } },
                            { title: t("bms_customers"), path: "/bms/customers", icon: { default: "tb-user", active: "tb-user-filled" } },
                            { title: t("bms_services"), path: "/bms/services", icon: { default: "tb-briefcase", active: "tb-briefcase-filled" } },
                            { title: t("bms_bookings"), path: "/bms/bookings", icon: { default: "tb-list", active: "tb-list" } },
                            { title: t("bms_booking_configurations"), path: "/bms/booking-configurations", icon: { default: "tb-settings", active: "tb-settings-filled" } },
                            { title: t("bms_calendar"), path: "/bms/calendar", icon: { default: "tb-calendar", active: "tb-calendar" } },
                        ]
                    }]
                    : []),
                ...(!userRoles.includes("company_owner") && userRoles.includes("company_specialist")
                    ? [{
                        title: t("bms"),
                        path: "/bms/bookings",
                        icon: {
                            default: "tb-calendar-event",
                            active: "tb-calendar-event-filled",
                        },
                        children: [
                            { title: t("bms_bookings"), path: "/bms/bookings", icon: { default: "tb-list", active: "tb-list" } },
                            { title: t("bms_calendar"), path: "/bms/calendar", icon: { default: "tb-calendar", active: "tb-calendar" } },
                            { title: t("bms_my_availability"), path: "/bms/my-availability", icon: { default: "tb-clock", active: "tb-clock" } },
                        ]
                    }]
                    : []),
                ...(userRoles.includes("company_owner")
                    ? [{
                        title: t("subscription"),
                        path: "/subscription",
                        icon: {
                            default: "tb-credit-card",
                            active: "tb-credit-card-filled",
                        },
                    }]
                    : []),
            ];
        }

        if (userRoles.includes("personal")) {
            return [
                {
                    title: t("dashboard"),
                    path: "/dashboard",
                    icon: {
                        default: "rb-chatbot",
                        active: "rb-chatbot-filled",
                    },
                },
                {
                    title: t("chat"),
                    path: "/personal/chat",
                    icon: {
                        default: "tb-cpu",
                        active: "tb-cpu-filled",
                    },
                },
            ];
        }

        // Marketing Specialist (default) - no mockup marketing dashboard
        return [
            {
                title: t("dashboard"),
                path: "/dashboard",
                icon: {
                    default: "tb-layout2",
                    active: "tb-layout2-filled",
                },
            },
        ];
    };

    return { menuItems: getMenuItems() };
};
